import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, ArrowUpRight, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import { THERAPISTS } from "@/lib/constants";
import { absoluteUrl } from "@/lib/seo-config";
import { track } from "@/lib/analytics";

// C1 — Therapist matcher quiz. Service-need based (Option 1a).
// Each therapist has a tag profile; we score each answer's tag against the
// therapist tags. Ashlan reserves the right to reassign — disclaimer below.

const THERAPIST_TAGS = {
  "glenn-robinson": [
    "chronic_pain", "sciatica", "back_neck", "lower_body", "sports", "rehab",
    "deep_pressure", "first_visit_ok",
  ],
  "teshea-pitt": [
    "headaches", "stress", "neck_shoulders", "shiatsu", "pregnancy", "tension",
    "firm_pressure", "first_visit_ok",
  ],
  "holly-paiva": [
    "sleep", "reflexology", "stress", "nervous_system", "pregnancy", "recovery",
    "light_pressure", "first_visit_ok",
  ],
};

const QUESTIONS = [
  {
    key: "goal",
    title: "What brings you in?",
    options: [
      { label: "Chronic or recurring pain", tags: ["chronic_pain", "rehab"] },
      { label: "Sports or training recovery", tags: ["sports", "rehab", "lower_body"] },
      { label: "Pregnancy-related discomfort", tags: ["pregnancy"] },
      { label: "Stress, headaches or poor sleep", tags: ["stress", "headaches", "sleep", "nervous_system"] },
      { label: "General wellness & maintenance", tags: ["recovery", "tension"] },
    ],
  },
  {
    key: "area",
    title: "Main body area?",
    options: [
      { label: "Back or neck", tags: ["back_neck"] },
      { label: "Shoulders or arms", tags: ["neck_shoulders"] },
      { label: "Lower body (hips, legs, feet)", tags: ["lower_body"] },
      { label: "Head, jaw or eyes", tags: ["headaches"] },
      { label: "Full body / not sure", tags: [] },
    ],
  },
  {
    key: "pressure",
    title: "Pressure preference?",
    options: [
      { label: "Light & calming", tags: ["light_pressure"] },
      { label: "Firm but not painful", tags: ["firm_pressure"] },
      { label: "Deep & therapeutic", tags: ["deep_pressure"] },
      { label: "I'd like the therapist to decide", tags: [] },
    ],
  },
  {
    key: "first",
    title: "Is this your first visit to Ashlan Clinic?",
    options: [
      { label: "Yes — first time", tags: ["first_visit_ok"] },
      { label: "No — returning client", tags: [] },
    ],
  },
];

const RATIONALES = {
  "glenn-robinson": "Glenn's neuromuscular and rehab focus makes him a strong fit for chronic pain, sciatica and sports recovery.",
  "teshea-pitt": "Teshea blends clinical massage with Shiatsu — well suited to headaches, stress patterns and pregnancy discomfort.",
  "holly-paiva": "Holly's reflexology + sleep-science background works well for nervous-system regulation, sleep wellness and gentle recovery.",
};

function scoreTherapists(answers) {
  const allTags = Object.values(answers).flatMap((o) => o?.tags || []);
  return THERAPISTS.map((t) => {
    const profile = THERAPIST_TAGS[t.slug] || [];
    const score = allTags.filter((tag) => profile.includes(tag)).length;
    return { ...t, score };
  })
    .sort((a, b) => b.score - a.score);
}

export default function TherapistMatch() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const q = QUESTIONS[step];
  const total = QUESTIONS.length;
  const pick = (option) => {
    const next = { ...answers, [q.key]: option };
    setAnswers(next);
    if (step + 1 < total) {
      setStep(step + 1);
    } else {
      setDone(true);
      track("therapist_match_complete", { answers: Object.keys(next).length });
    }
  };
  const back = () => setStep(Math.max(0, step - 1));
  const restart = () => { setStep(0); setAnswers({}); setDone(false); };

  return (
    <main data-testid="therapist-match-page" className="bg-white min-h-screen">
      <SEO
        title="Find your therapist — Ashlan Clinic"
        description="A short quiz to help you pick the right Ashlan Clinic therapist for your needs — chronic pain, sports recovery, pregnancy or sleep wellness."
        path="/match"
        noIndex
      />
      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-10">
          <span className="ash-eyebrow">Therapist Matcher</span>
          <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#080808]">
            Four short questions. One good starting point.
          </h1>
          <p className="mt-6 text-lg text-[#3C4048] leading-relaxed">
            Pick the option closest to what you need. We&apos;ll suggest the therapist who fits best — Ashlan may reassign based on availability and clinical judgement.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {!done ? (
            <div data-testid="quiz-step" className="border border-[#e5e7eb] bg-white p-8 md:p-12">
              <div className="flex items-center justify-between mb-6">
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3A7A8C]">
                  Step {step + 1} of {total}
                </span>
                <div className="h-1 flex-1 mx-6 bg-[#F5F5F5] overflow-hidden">
                  <div className="h-full bg-[#5FA8B4] transition-all"
                    style={{ width: `${((step + 1) / total) * 100}%` }} />
                </div>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#080808] tracking-tight">
                {q.title}
              </h2>
              <div className="mt-8 grid gap-3" data-testid="quiz-options">
                {q.options.map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => pick(opt)}
                    data-testid={`opt-${q.key}-${opt.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-left p-5 border border-[#e5e7eb] hover:border-[#3A7A8C] hover:bg-[#F0F7F9] text-[#080808] font-semibold transition-colors flex items-center gap-3"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#5FA8B4]" />
                    {opt.label}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button type="button" onClick={back} data-testid="quiz-back" className="mt-8 ash-link-underline inline-flex items-center gap-1.5">
                  <ArrowLeft size={14} /> Back
                </button>
              )}
            </div>
          ) : (
            <Result answers={answers} restart={restart} />
          )}
        </div>
      </section>
    </main>
  );
}

function Result({ answers, restart }) {
  const ranked = scoreTherapists(answers);
  const top = ranked[0];
  const second = ranked[1];

  return (
    <div data-testid="quiz-result" className="border border-[#3A7A8C] bg-[#F0F7F9] p-8 md:p-12">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={16} className="text-[#3A7A8C]" />
        <span className="ash-eyebrow">Your match</span>
      </div>
      <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#080808] leading-tight">
        Based on your answers, start with <span className="text-[#3A7A8C]">{top.name}</span>.
      </h2>
      <p className="mt-4 text-[#3C4048] leading-relaxed">{RATIONALES[top.slug]}</p>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 flex-wrap">
        <a
          href={top.bookingUrl}
          data-testid="match-book-top"
          className="ash-btn-primary"
        >
          Book with {top.name.split(" ")[0]} <ArrowUpRight size={16} />
        </a>
        <Link to={`/team#${top.slug}`} className="ash-btn-secondary" data-testid="match-view-bio">
          See full bio <ArrowRight size={16} />
        </Link>
        <button type="button" onClick={restart} data-testid="match-restart" className="ash-link-underline ml-auto">
          Retake the quiz
        </button>
      </div>

      {second && second.score > 0 && (
        <div className="mt-10 pt-8 border-t border-[#3A7A8C]/30" data-testid="match-second">
          <div className="ash-eyebrow mb-2">Also a good fit</div>
          <p className="text-[#080808] font-semibold">{second.name}</p>
          <p className="text-sm text-[#3C4048] mt-1">{RATIONALES[second.slug]}</p>
          <a href={second.bookingUrl} className="ash-link-underline mt-3 inline-flex items-center gap-1.5">
            Book with {second.name.split(" ")[0]} <ArrowUpRight size={12} />
          </a>
        </div>
      )}

      <p className="mt-10 text-xs text-[#6B7280] leading-relaxed border-t border-[#3A7A8C]/30 pt-6">
        <Check size={12} className="inline mr-1" />
        Final therapist assignment is at Ashlan&apos;s discretion based on availability, your clinical history, and the lead therapist&apos;s judgement. Not a medical recommendation.
      </p>

      <div className="hidden">{absoluteUrl("/")}</div>
    </div>
  );
}
